import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

/**
 * UndoContext
 * ---------------------------------------------------------------------------
 * Captures the most recent destructive action and exposes it for ⌘Z restore.
 *
 * Usage from any handler:
 *   const { capture } = useUndo();
 *   capture({
 *     label: 'Task archived',   // shown on the undo toast
 *     undo: async () => { ... }, // async restore fn
 *   });
 *
 * The hook also wires a global ⌘Z (Ctrl+Z) listener that calls the undo fn
 * for whatever's in the buffer. Only the LAST captured action is undoable.
 */

const UndoContext = createContext(null);
const UNDO_TTL_MS = 8000;

export function UndoProvider({ children }) {
  const bufferRef = useRef(null); // { label, undo, expiresAt }
  const timerRef = useRef(null);

  const clear = useCallback(() => {
    bufferRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runItem = useCallback(async (item) => {
    // Capture-bound undo (ARCH-NOTE-2 fix): each toast's Undo button acts on
    // the action it announced — never on whatever happens to be newest.
    if (!item || item.done || Date.now() > item.expiresAt) return;
    item.done = true;
    if (bufferRef.current === item) clear();
    try {
      await item.undo();
      toast.success('Undone');
    } catch {
      toast.error('Could not undo');
    }
  }, [clear]);

  const runUndo = useCallback(async () => {
    await runItem(bufferRef.current);
  }, [runItem]);

  const capture = useCallback(({ label, undo }) => {
    if (typeof undo !== 'function') return;
    clear();
    const item = { label, undo, expiresAt: Date.now() + UNDO_TTL_MS, done: false };
    bufferRef.current = item;
    timerRef.current = setTimeout(clear, UNDO_TTL_MS);

    // Sonner action toast with Undo button + auto-dismiss aligned to TTL.
    // onClick closes over THIS item (not the shared buffer).
    toast(label || 'Action complete', {
      action: {
        label: 'Undo',
        onClick: () => { void runItem(item); },
      },
      duration: UNDO_TTL_MS,
    });
  }, [clear, runItem]);

  // Global keyboard listener: ⌘Z / Ctrl+Z (not Shift)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        const tag = document.activeElement?.tagName;
        const isEditable = document.activeElement?.isContentEditable;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isEditable) return;
        if (!bufferRef.current) return;
        e.preventDefault();
        void runUndo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [runUndo]);

  const value = useMemo(() => ({ capture, runUndo, clear }), [capture, runUndo, clear]);
  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>;
}

export function useUndo() {
  const ctx = useContext(UndoContext);
  if (!ctx) {
    // Safe no-op if a caller is outside the provider tree (e.g. tests).
    return { capture: () => {}, runUndo: () => {}, clear: () => {} };
  }
  return ctx;
}
